import { Activity, CalendarRange, Inbox, MessageSquarePlus, Sparkles } from "lucide-react";
import { Dialog as RD } from "radix-ui";
import { Suspense, lazy } from "react";
import { openAskAi, registerAiDraftSheet, registerAskAiPanel, type AiDraftSheetProps, type AskAiPanelProps } from "@/app/shell/ask-ai";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { defineFeature } from "../types";

/**
 * "ASK YOUR BUSINESS" — roadmap item 14 (PRD §9, docs/product/ai-assistant-spec.md).
 *
 *  · The Ask panel fills the shell's "Ask AI" drawer (topbar button, ⌘J, ⌘K "Ask …").
 *  · The draft sheet fills the shell's AI draft slot, which record pages open
 *    through `@/app/shell/ai-actions` ("Draft reminder", "Summarise", "Weekly report").
 *  · /ai is the full-page Ask plus the review inbox, the weekly reports and the
 *    admin activity ledger; Settings → AI assistant holds the switches and the meter.
 *  · /portal/weekly-reports is what the client reads once a report is published.
 *
 * Both slot renderers are registered here, at module load, as LAZY components:
 * the registry imports this file statically, so anything heavier than a
 * `lazy()` wrapper would land in the entry chunk.
 */

const AskPanel = lazy(() => import("./ask-panel"));
const DraftSheet = lazy(() => import("./draft-sheet"));
const AiSettingsPage = lazy(() => import("./settings"));

/** Plain markup on purpose (no <Skeleton>): this module is in the entry chunk. */
function PanelFallback() {
  return (
    <div className="grid gap-4 px-6 py-6" role="status" aria-label="Loading the assistant">
      <div className="skeleton-shimmer size-11 rounded-card" />
      <div className="skeleton-shimmer h-6 w-2/3 rounded-item" />
      <div className="skeleton-shimmer h-4 w-full rounded-item" />
      <div className="skeleton-shimmer h-4 w-4/5 rounded-item" />
    </div>
  );
}

function AskPanelSlot(props: AskAiPanelProps) {
  return (
    <Suspense fallback={<PanelFallback />}>
      <AskPanel {...props} />
    </Suspense>
  );
}

function DraftSheetSlot(props: AiDraftSheetProps) {
  return (
    <Suspense
      fallback={
        <Sheet open={props.open} onOpenChange={props.onOpenChange}>
          <SheetContent size="lg">
            <RD.Title className="sr-only">AI draft</RD.Title>
            <RD.Description className="sr-only">Loading the draft.</RD.Description>
            <PanelFallback />
          </SheetContent>
        </Sheet>
      }
    >
      {/* One instance per request, so a new record never flashes the previous draft. */}
      <DraftSheet key={props.requestId} {...props} />
    </Suspense>
  );
}

registerAskAiPanel(AskPanelSlot);
registerAiDraftSheet(DraftSheetSlot);

function SettingsFallback() {
  return (
    <div className="grid w-full gap-5" role="status" aria-label="Loading AI settings">
      <div className="skeleton-shimmer h-6 w-56 rounded-item" />
      <div className="skeleton-shimmer h-40 w-full rounded-card" />
      <div className="skeleton-shimmer h-64 w-full rounded-card" />
    </div>
  );
}

export default defineFeature({
  key: "ai",
  title: "Ask your business",
  routes: [
    { path: "ai", lazy: () => import("./page") },
    { path: "ai/drafts", lazy: () => import("./drafts") },
    { path: "ai/reports", lazy: () => import("./reports") },
    { path: "ai/reports/:id", lazy: () => import("./report-detail") },
    { path: "ai/activity", lazy: () => import("./activity") },
  ],
  portalRoutes: [
    { path: "weekly-reports", lazy: () => import("./portal-reports") },
    { path: "weekly-reports/:id", lazy: () => import("./portal-report") },
  ],
  nav: [
    {
      key: "ai-review",
      label: "AI review",
      to: "/ai/drafts",
      icon: Inbox,
      group: "clients",
      order: 45,
      permission: "ai:use",
      description: "Drafted reminders, summaries and weekly reports waiting for you",
      badgeQueryKey: ["ai", "drafts", "pending", "all"],
      badgeTone: "primary",
    },
  ],
  commands: [
    {
      key: "ai.ask",
      label: "Ask your business a question",
      icon: Sparkles,
      run: ({ close }) => {
        close();
        openAskAi();
      },
      group: "actions",
      permission: "ai:use",
      shortcut: "mod+j",
      keywords: ["ai", "assistant", "insights", "chat", "question", "gpt"],
    },
    {
      key: "ai.new",
      label: "New AI conversation (full page)",
      icon: MessageSquarePlus,
      to: "/ai",
      group: "navigate",
      permission: "ai:use",
      keywords: ["ai", "assistant", "history"],
    },
    {
      key: "ai.reports",
      label: "Weekly client reports",
      icon: CalendarRange,
      to: "/ai/reports",
      group: "navigate",
      permission: ["ai:use", "clients:read"],
      keywords: ["weekly", "report", "portal", "publish", "client update"],
    },
    {
      key: "ai.activity",
      label: "AI activity log",
      icon: Activity,
      to: "/ai/activity",
      group: "navigate",
      permission: "audit:view",
      keywords: ["ai", "ledger", "usage", "cost", "tokens", "audit"],
    },
  ],
  settingsSections: [
    {
      key: "ai",
      label: "AI assistant",
      path: "ai",
      group: "Workspace",
      order: 150,
      icon: Sparkles,
      permission: "org:manage",
      description: "Switch the assistant on, choose what it may read, and cap what it spends",
      element: (
        <Suspense fallback={<SettingsFallback />}>
          <AiSettingsPage />
        </Suspense>
      ),
    },
  ],
});
